import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { EmployeeViewModal } from "@/pages/employees/widgets/EmployeeViewModal.tsx";
import { useRemindersHistory, useReminderSend } from "@/queries/reminders.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiEmployee, ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import {
  ApiNotificationChannelType,
  getApiNotificationChannelTypeTitle,
} from "@/types/notification.types.ts";
import { ApiReminderHistory, ApiReminderHistoryFilter } from "@/types/reminder.types.ts";
import {
  Box,
  DropdownMenu,
  DropdownMenuItem,
  Modal,
  ModalProps,
  Pagination,
  Skeleton,
  Stack,
  Table,
  TableCell,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { twMerge } from "tailwind-merge";

export interface ReminderHistoryModalProps extends ModalProps {
  filter: Omit<ApiReminderHistoryFilter, "page_number">;
  hideRemindActions?: boolean;
}

export function ReminderHistoryModal({
  filter,
  hideRemindActions,
  ...props
}: ReminderHistoryModalProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderDate, renderDateAndTime } = useFormatter();
  const sendReminder = useReminderSend();

  const [page, setPage] = useState(1);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reminders.historyViewed, {
      [analyticProperties.source]: filter.entity_type,
    });
  });

  const { data, isPending } = useRemindersHistory({
    ...filter,
    page_number: page,
  });

  const handleRemind = useCallback(
    async (item: ApiReminderHistory) => {
      try {
        await sendReminder.mutateAsync({
          entity_type: filter.entity_type,
          entity_ids: [item.entity_id],
          recipient_employee_ids: [item.recipient.id],
          channels: [
            ApiNotificationChannelType.in_app,
            ApiNotificationChannelType.email,
            ApiNotificationChannelType.slack,
          ],
          custom_subject: null,
          custom_body: null,
        });
        analyticsService.trackEvent(analyticEvents.reminders.sent, {
          [analyticProperties.source]: filter.entity_type,
        });
      } catch {
        return;
      }
    },
    [filter.entity_type, sendReminder]
  );

  const columnHelper = createColumnHelper<ApiReminderHistory>();

  const columns = useMemo<ColumnDef<ApiReminderHistory>[]>(() => {
    return [
      columnHelper.display({
        header: t("common:date"),
        cell: (row) => {
          const date = new Date(row.row.original.datetime_sent_at);
          return (
            <Tooltip>
              <TooltipTrigger>
                <TableCell variant={"secondary"}>{renderDate(date)}</TableCell>
              </TooltipTrigger>
              <TooltipContent>{renderDateAndTime(date)}</TooltipContent>
            </Tooltip>
          );
        },
        ...ColumnSizes.date,
      }),
      columnHelper.display({
        header: t("core:full_name"),
        cell: (row) => (
          <EmployeeHoverCard
            employee={row.row.original.recipient as ApiEmployee}
            position={getEmployeePositionTitle(row.row.original.recipient as ApiEmployee)}
          >
            <EmployeeItem
              employee={row.row.original.recipient as ApiEmployee}
              showPosition={false}
            />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.employee,
      }),
      columnHelper.display({
        header: t("core:sender"),
        cell: (row) => (
          <EmployeeHoverCard employee={row.row.original.author}>
            <EmployeeCell
              employee={row.row.original.author}
              onClick={() =>
                openModal(EmployeeViewModal, { employeeId: row.row.original.author.id })
              }
            />
          </EmployeeHoverCard>
        ),
        ...ColumnSizes.standard,
      }),
      columnHelper.display({
        header: t("core:channels"),
        cell: (row) => (
          <TableCell variant={"secondary"}>
            {row.row.original.channels.map((c) => getApiNotificationChannelTypeTitle(c)).join(", ")}
          </TableCell>
        ),
        ...ColumnSizes.standard,
      }),
    ];
  }, [t, renderDate, renderDateAndTime, openModal]);

  const renderRowAction = useCallback(
    (row: Row<ApiReminderHistory>) => (
      <DropdownMenu>
        {!hideRemindActions && (
          <DropdownMenuItem onClick={() => handleRemind(row.original)} title={t("core:remind")} />
        )}
        <DropdownMenuItem
          onClick={() => openModal(EmployeeViewModal, { employeeId: row.original.recipient.id })}
          title={t("core:view_recipient_profile")}
        />
        <DropdownMenuItem
          onClick={() => openModal(EmployeeViewModal, { employeeId: row.original.author.id })}
          title={t("core:view_sender_profile")}
        />
      </DropdownMenu>
    ),
    [handleRemind, hideRemindActions, openModal, t]
  );

  const metadata = data?.metadata;
  const showingLabel = useMemo(() => {
    if (!metadata) return "";
    const { page_number, items_per_page, items, total_items } = metadata;
    const from = (page_number - 1) * items_per_page + 1;
    const to = (page_number - 1) * items_per_page + items;
    return t("common:pagination_pages", { from, to, total: total_items });
  }, [metadata, t]);

  const caption = isPending
    ? t("common:loading")
    : t("plurals:reminders_sent_count", { count: data?.metadata.total_items ?? 0 });

  return (
    <Modal
      {...props}
      size={"xl"}
      title={t("core:reminder_history")}
      caption={caption}
      withCloser
      closeByOutsidePress
      closeByEscEnabled
    >
      <Box className={"relative min-h-[200px]"}>
        {isPending && (
          <Stack gap={"sm"}>
            <Skeleton className={"h-12 w-full"} />
            <Skeleton className={"h-12 w-full"} />
            <Skeleton className={"h-12 w-full"} />
          </Stack>
        )}
        {!isPending && data && (
          <>
            <Table
              data={data.items}
              columns={columns}
              renderRowAction={renderRowAction}
              emptyStateComponent={<EmptyStateNothingFound />}
            />
            {data.metadata.total_pages > 1 && (
              <Pagination
                showingLabel={showingLabel}
                currentPage={page}
                totalPages={data.metadata.total_pages}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  );
}

function EmployeeCell({ employee, onClick }: { employee: ApiEmployeeShort; onClick?: () => void }) {
  const { renderEmployeeName } = useFormatter();
  return (
    <Stack
      direction={"horizontal"}
      gap={"sm"}
      items={"center"}
      className={twMerge(
        "overflow-hidden text-ellipsis whitespace-nowrap",
        onClick ? "cursor-pointer" : ""
      )}
      onClick={onClick}
    >
      <EmployeeAvatar employee={employee} size={"sm"} />
      <Text className={'text-secondary overflow-hidden text-ellipsis whitespace-nowrap"'}>
        {renderEmployeeName(employee)}
      </Text>
    </Stack>
  );
}
